// The DELETE THE ACCOUNT section of the ACCOUNT page of the hub SETTINGS
// panel (deployment.md §10.4), in the look of the ☸ OPTIONS menu (R1731).
//
// What it shows: one line that says what the delete removes, a box for
// the account name, a box for the password when the account holds the
// email method, and one control. The control takes two presses through
// the `ArmGate` of every destructive control (R837). The first press
// arms it only when the box holds the account name, so a player learns
// of a typing error before the call and not from a refusal.
//
// The call is `DELETE /api/me`. It clears the session cookie, so on
// success the section clears every resume token and reloads, which is the
// way back to the landing screen the sign-out takes as well.

import { CHROME_ARM_MS } from '../../../gen/presentation';
import { deleteMe, messageOf } from '../../../net/api';
import { clearAllTokens } from '../../../net/token';
import { hubSettings as S } from '../../../gen/strings';
import { ArmGate } from '../../arm';
import { note } from '../../options/kit';
import { button } from '../../choice/choice';
import { armedPress, textBox, wireOnce, type PanelContext } from '../panel';
import { errNote } from './optcards';
import { isReauth, reauthBlock } from './reauth';

/** The key of the one control the section arms. */
const KEY = 'erase';

/** The part of a panel the ACCOUNT page mounts. */
interface EraseSection {
  /** The inner markup of the section. `password` says whether the account
   *  holds the email method and so a password to state. `signingOut` is
   *  true while the sign-out of SIGN IN AGAIN runs. */
  html(password: boolean, signingOut?: boolean): string;
  /** True when the delete met `reauth_required` (R2963). The section then
   *  shows SIGN IN AGAIN, and the panel takes the press. */
  reauth(): boolean;
  /** Takes one press. Answers false for an action that is not the section's. */
  act(action: string, el: HTMLElement): boolean;
  /** Wires the two boxes after a render. */
  wire(root: HTMLElement): void;
  /** Puts the arm gate at rest, when the panel closes. */
  clear(): void;
}

/** True when `typed` names the account: the server compares without regard
 *  to case and to the space around the name, and so does this check. */
export function namesAccount(typed: string, name: string): boolean {
  return typed.trim().toLowerCase() === name.toLowerCase();
}

export function createEraseSection(
  ctx: PanelContext,
  leave: () => void = () => { clearAllTokens(); location.reload(); },
): EraseSection {
  const gate = new ArmGate(CHROME_ARM_MS, () => ctx.refresh());
  const text: Record<string, string> = {};
  let busy = false;
  let err = '';
  let reauth = false;

  function box(name: string, attrs: string, placeholder: string): string {
    return textBox(name, attrs, placeholder, text[name] ?? '');
  }

  /** The press. The first one arms, the second one calls. */
  function press(password: boolean): void {
    if (busy) return;
    const name = ctx.me().account.name;
    if (!namesAccount(text['erase-name'] ?? '', name)) {
      gate.clear(false);
      err = S.erase_mismatch;
      ctx.play('sfx-menu-error');
      ctx.refresh();
      return;
    }
    err = '';
    if (!armedPress(ctx, gate, KEY)) return;
    busy = true;
    ctx.refresh();
    void deleteMe(text['erase-name'] ?? '', password ? text['erase-password'] ?? '' : '').then(
      () => { leave(); },
      (e: unknown) => {
        // An Apple or Google account deletes itself only on a fresh
        // session (R2963): the SIGN IN AGAIN block says so, in place of a
        // line.
        reauth = isReauth(e);
        err = reauth ? '' : messageOf(e);
        busy = false;
        ctx.play('sfx-menu-error');
        ctx.refresh();
      },
    );
  }

  return {
    html(password: boolean, signingOut = false): string {
      const me = ctx.me();
      const guest = me.account.kind === 'guest';
      // Red at all times, and the armed state wears the red ground (R837).
      // The armed face names the act and shares one cell with DELETE
      // (R2069); the busy word stands alone.
      const armed = !busy && gate.is(KEY);
      return note(guest ? S.erase_lede_guest : S.erase_lede)
        + '<div class="c-form">'
        + box('erase-name', 'type="text" autocomplete="off"', S.erase_name_box({ name: me.account.name }))
        + (password
          ? box('erase-password', 'type="password" autocomplete="current-password"', S.erase_password_box)
          : '')
        + button(busy ? S.erase_busy : S.erase_go, { act: 'erase-go', id: password ? '1' : '' },
          { variant: 'danger', armFace: busy ? undefined : S.erase_armed, armed, disabled: busy })
        + '</div>'
        + (reauth ? reauthBlock(signingOut) : '')
        + errNote(err);
    },

    reauth(): boolean {
      return reauth;
    },

    act(action: string, el: HTMLElement): boolean {
      if (action !== 'erase-go') return false;
      press(el.dataset.id === '1');
      return true;
    },

    wire(root: HTMLElement): void {
      for (const el of root.querySelectorAll<HTMLInputElement>('input[data-in^="erase-"]')) {
        if (!wireOnce(el)) continue;
        const name = el.dataset.in ?? '';
        el.addEventListener('input', () => {
          text[name] = el.value;
          // A change of the text disarms the control: the second press
          // must confirm what the box holds now.
          if (gate.active) gate.clear();
        });
      }
    },

    clear(): void {
      gate.clear(false);
    },
  };
}
